import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Activity, BarChart3, Funnel as FunnelIcon, Globe, IndianRupee, LineChart as LineIcon,
  MapPinned, PieChart as PieIcon, Target as TargetIcon, Timer, TrendingDown, TrendingUp,
  Users, RefreshCw, Database,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line, FunnelChart, Funnel, LabelList, PieChart, Pie, Cell,
} from 'recharts';
import { toast } from 'sonner';
import GeoHeatmap from '../components/analytics/GeoHeatmap';
import AdminEntryPanel from '../components/analytics/AdminEntryPanel';
import LookerDocsPanel from '../components/analytics/LookerDocsPanel';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week',  label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom Range' },
];

const COLORS = ['#E31E24', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const KpiCard = ({ icon: Icon, label, value, suffix, color = 'text-primary', testid }) => (
  <Card data-testid={testid}>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}{suffix}</p>
        </div>
        <div className={`p-2 rounded-md bg-muted ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ReportsAnalyticsPage = () => {
  const { api, user } = useAuth();
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      let url = `/reports/dashboard?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url += `&startDate=${customStart}&endDate=${customEnd}`;
      }
      const res = await api.get(url);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, [period, customStart, customEnd]);

  const summary = data?.summary || {};
  const reports = data?.reports || {};
  const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-IN') : '-');
  const fmtMoney = (n) => (typeof n === 'number' ? '₹' + n.toLocaleString('en-IN') : '-');
  const fmtPct = (n) => (typeof n === 'number' ? n.toFixed(1) + '%' : '-');

  return (
    <div className="space-y-6" data-testid="reports-analytics-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Globe className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">Reports &amp; Analytics</h1>
            <Badge variant="secondary" data-testid="hidden-badge">Restricted</Badge>
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Leadership dashboard — 17 strategic reports + geographical hotspot + Looker Studio integration.
            Visible only to Super Admin, CEO &amp; Manager.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[160px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="w-[150px]"
                placeholder="Start date"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="w-[150px]"
                placeholder="End date"
              />
            </div>
          )}
          <Button variant="outline" size="icon" onClick={fetchDashboard} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Signed in as</p>
            <Badge data-testid="reports-role-badge">{user?.role}</Badge>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="kpi-row">
        <KpiCard testid="kpi-total-leads" icon={Users} label="Total Leads" value={fmt(summary.totalLeads)} />
        <KpiCard testid="kpi-qualified" icon={TrendingUp} label="Qualified +" value={fmt(summary.qualifiedOrBeyond)} suffix={` (${fmtPct(summary.qualifiedPct)})`} color="text-emerald-600" />
        <KpiCard testid="kpi-closures" icon={TargetIcon} label="Closures" value={fmt(summary.closures)} color="text-blue-600" />
        <KpiCard testid="kpi-mkt-spend" icon={IndianRupee} label="Mkt Spend" value={fmtMoney(summary.totalMarketingSpend)} color="text-orange-600" />
        <KpiCard testid="kpi-cpa" icon={IndianRupee} label="CPA" value={summary.cpa ? fmtMoney(Math.round(summary.cpa)) : '-'} color="text-rose-600" />
        <KpiCard testid="kpi-sales-cycle" icon={Timer} label="Avg Cycle" value={fmt(summary.avgSalesCycleDays)} suffix=" d" color="text-violet-600" />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="dashboards" className="w-full">
        <TabsList className="grid w-full grid-cols-4" data-testid="reports-tabs">
          <TabsTrigger value="dashboards" data-testid="tab-dashboards">Dashboards</TabsTrigger>
          <TabsTrigger value="geo" data-testid="tab-geo">Geo Heatmap</TabsTrigger>
          <TabsTrigger value="admin" data-testid="tab-admin">Admin Entry</TabsTrigger>
          <TabsTrigger value="looker" data-testid="tab-looker">Looker Studio</TabsTrigger>
        </TabsList>

        {/* ============= DASHBOARDS TAB ============= */}
        <TabsContent value="dashboards" className="space-y-4 mt-4">

          {/* Row 1 — Funnel + Conversion Trend */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="report-funnel">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><FunnelIcon className="h-4 w-4 text-primary" /> Weekly Funnel</CardTitle>
                <CardDescription>Lead count by stage. Bottleneck: <strong>{reports.bottleneck?.stage || '-'}</strong> ({reports.bottleneck?.count || 0})</CardDescription>
              </CardHeader>
              <CardContent style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reports.funnel || []} layout="vertical" margin={{ left: 60, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="stage" type="category" width={110} fontSize={11} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#E31E24" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid="report-conversion-trend">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><LineIcon className="h-4 w-4 text-primary" /> Conversion Trend</CardTitle>
                <CardDescription>New leads vs closures by day</CardDescription>
              </CardHeader>
              <CardContent style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={reports.conversionTrends || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" fontSize={10} />
                    <YAxis fontSize={10} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="newLeads" stroke="#3B82F6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="closed" stroke="#10B981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Row 2 — Lead Source Performance + Marketing Quality */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="report-source-performance">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><PieIcon className="h-4 w-4 text-primary" /> Lead Source Performance</CardTitle>
                <CardDescription>Leads, closures &amp; CPA per source</CardDescription>
              </CardHeader>
              <CardContent style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reports.leadSourcePerformance || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="source" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="leads" fill="#3B82F6" name="Leads" />
                    <Bar dataKey="qualified" fill="#F59E0B" name="Qualified" />
                    <Bar dataKey="closed" fill="#10B981" name="Closed" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid="report-marketing-quality">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" /> Marketing Lead Quality</CardTitle>
                <CardDescription>Qualified % per source</CardDescription>
              </CardHeader>
              <CardContent style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reports.marketingLeadQuality || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="source" fontSize={11} />
                    <YAxis fontSize={11} unit="%" />
                    <Tooltip formatter={(v) => `${(+v).toFixed(1)}%`} />
                    <Bar dataKey="qualityPct" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Row 3 — Lead Aging + Dropout Ratio */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="report-lead-aging">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Timer className="h-4 w-4 text-violet-600" /> Lead Aging by Stage</CardTitle>
                <CardDescription>Average days a lead stays at each stage</CardDescription>
              </CardHeader>
              <CardContent style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reports.leadAging || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" fontSize={10} angle={-25} textAnchor="end" height={70} />
                    <YAxis fontSize={11} unit=" d" />
                    <Tooltip />
                    <Bar dataKey="avgDays" fill="#8B5CF6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid="report-dropout-ratio">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><TrendingDown className="h-4 w-4 text-rose-600" /> Dropout Ratio</CardTitle>
                <CardDescription>% of leads going to Dead / Nurture from each stage</CardDescription>
              </CardHeader>
              <CardContent style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reports.dropoutRatio || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" fontSize={10} angle={-25} textAnchor="end" height={70} />
                    <YAxis fontSize={11} unit="%" />
                    <Tooltip formatter={(v) => `${(+v).toFixed(1)}%`} />
                    <Bar dataKey="dropoutPct" fill="#EC4899" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Row 4 — Target vs Achievement + Spend by Source */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="report-target-vs-achievement">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><TargetIcon className="h-4 w-4 text-blue-600" /> Target vs Achievement</CardTitle>
                <CardDescription>Active targets overlapping selected period</CardDescription>
              </CardHeader>
              <CardContent>
                {(!reports.targetVsAchievement || reports.targetVsAchievement.length === 0) ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">No active targets in this period.<br />Set targets in <strong>Admin Entry → Targets</strong>.</p>
                ) : (
                  <div className="space-y-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {reports.targetVsAchievement.map((t) => (
                      <div key={t.id} className="border rounded-md p-3">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium">{t.executiveName} — <span className="text-muted-foreground capitalize">{t.metric}</span></p>
                          <Badge variant={t.achievementPct >= 100 ? 'default' : 'outline'}>{fmtPct(t.achievementPct)}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mb-1">{t.periodStart} → {t.periodEnd}</div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary" style={{ width: `${Math.min(100, t.achievementPct)}%` }} />
                        </div>
                        <div className="text-xs mt-1 flex justify-between"><span>Achieved: <strong>{fmt(t.achieved)}</strong></span><span>Target: <strong>{fmt(t.target)}</strong></span></div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="report-spend-breakdown">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><IndianRupee className="h-4 w-4 text-orange-600" /> Marketing Spend by Source</CardTitle>
                <CardDescription>Total: {fmtMoney(reports.totalMarketingSpend?.total || 0)} · Payroll: {fmtMoney(reports.salesPayrollCost?.total || 0)}</CardDescription>
              </CardHeader>
              <CardContent style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={reports.totalMarketingSpend?.bySource || []} dataKey="amount" nameKey="source" outerRadius={90} label={(e) => e.source}>
                      {(reports.totalMarketingSpend?.bySource || []).map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtMoney(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Row 5 — Mini Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="report-mini-stats">
            <Card><CardContent className="pt-5">
              <p className="text-xs text-muted-foreground uppercase">Presentation vs Visit</p>
              <p className="text-lg font-bold mt-1">{fmt(reports.presentationVsVisit?.presentation)} → {fmt(reports.presentationVsVisit?.visitConfirmed)}</p>
              <p className="text-[11px] text-muted-foreground">Virtual Meet → Awaited Visit</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5">
              <p className="text-xs text-muted-foreground uppercase">Visit → Turnup Rate</p>
              <p className="text-lg font-bold mt-1">{fmtPct(reports.visitVsTurnup?.rate || 0)}</p>
              <p className="text-[11px] text-muted-foreground">{reports.visitVsTurnup?.turnup || 0} of {reports.visitVsTurnup?.scheduled || 0} scheduled</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5">
              <p className="text-xs text-muted-foreground uppercase">Level-2 Rejection</p>
              <p className="text-lg font-bold mt-1">{fmt(reports.level2Rejection?.count || 0)}</p>
              <p className="text-[11px] text-muted-foreground">Qualified leads → Dead/Nurture</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5">
              <p className="text-xs text-muted-foreground uppercase">Leads vs Qualified</p>
              <p className="text-lg font-bold mt-1">{fmt(reports.leadsVsQualified?.total)} → {fmt(reports.leadsVsQualified?.qualified)}</p>
              <p className="text-[11px] text-muted-foreground">Overall qualification</p>
            </CardContent></Card>
          </div>

        </TabsContent>

        {/* ============= GEO TAB ============= */}
        <TabsContent value="geo" className="mt-4">
          <GeoHeatmap api={api} period={period} />
        </TabsContent>

        {/* ============= ADMIN ENTRY TAB ============= */}
        <TabsContent value="admin" className="mt-4">
          <AdminEntryPanel api={api} onChanged={fetchDashboard} />
        </TabsContent>

        {/* ============= LOOKER TAB ============= */}
        <TabsContent value="looker" className="mt-4">
          <LookerDocsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsAnalyticsPage;
